<template>
  <div class="group-card" :class="{ expanded }">
    <!-- Collapsed header -->
    <div class="group-header" @click="toggleExpand">
      <div class="group-header-left">
        <div class="group-icon">
          <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="M2 13V6l6-4 6 4v7"/>
            <path d="M6 13v-4h4v4"/>
          </svg>
        </div>
        <div>
          <div class="group-name">{{ group.name }}</div>
          <div class="group-subtitle">
            {{ group.member_count }} linked
            <span v-if="ownerLine"> · Owner: {{ ownerLine }}</span>
          </div>
        </div>
      </div>
      <div class="group-header-actions" @click.stop>
        <button class="btn btn-outline btn-sm" @click="toggleExpand">{{ expanded ? 'Close' : 'Edit' }}</button>
        <button class="btn btn-danger btn-sm" @click="onDelete">Remove</button>
      </div>
    </div>

    <!-- Expanded detail -->
    <div v-if="expanded" class="group-detail">

      <!-- Properties in this group -->
      <div class="detail-section">
        <div class="detail-section-title">Properties in this group</div>
        <div class="group-props">
          <div v-for="(membership, i) in group.memberships" :key="membership.id" class="group-prop-row">
            <div class="prop-row-left">
              <div class="prop-badge">{{ 'P' + (i + 1) }}</div>
              <span class="prop-name">{{ membership.property_name }}</span>
            </div>
            <button class="btn btn-danger btn-sm" @click="onRemoveMember(membership.property_id)">
              Remove
            </button>
          </div>
          <div v-if="group.memberships.length === 0" class="empty-line">
            No properties yet — add one below.
          </div>
        </div>
        <div class="add-member-row">
          <select v-model="memberPick" class="setting-input" @change="addMemberError = ''">
            <option value="" disabled>Add a property…</option>
            <option v-for="p in availableForAdd" :key="p.id" :value="p.id">{{ p.name }}</option>
          </select>
          <button class="btn btn-outline btn-sm" :disabled="!memberPick || addingMember" @click="onAddMember">
            <svg viewBox="0 0 14 14" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="7" y1="1" x2="7" y2="13"/>
              <line x1="1" y1="7" x2="13" y2="7"/>
            </svg>
            {{ addingMember ? 'Adding…' : 'Add property' }}
          </button>
        </div>
        <div v-if="addMemberError" class="error-banner inline-error">{{ addMemberError }}</div>
      </div>

      <!-- Shared costs for this group -->
      <div class="detail-section">
        <div class="detail-section-title">Shared costs for this group</div>
        <div class="shared-costs-grid">
          <div class="field">
            <label class="field-label">Cleaning fee (total for group)</label>
            <div class="input-prefix">
              <span>£</span>
              <input
                v-model.number="editable.shared_cleaning_fee"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                class="setting-input"
              />
            </div>
            <div class="field-hint">Single cleaning fee split across all properties in the report</div>
          </div>
          <div class="field">
            <label class="field-label">Laundry &amp; linen (total for group)</label>
            <div class="input-prefix">
              <span>£</span>
              <input
                v-model.number="editable.shared_laundry_fee"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                class="setting-input"
              />
            </div>
            <div class="field-hint">Single laundry charge applied at group level</div>
          </div>
          <div class="field">
            <label class="field-label">Split method</label>
            <select v-model="editable.split_method" class="setting-input">
              <option value="equal">Split equally between properties</option>
              <option value="single_line">Show as single line in report</option>
              <option value="pro_rata_revenue">Pro-rata by booking revenue</option>
            </select>
            <div class="field-hint">How shared costs appear in the combined report</div>
          </div>
        </div>

        <!-- Primary property selector (single_line only) -->
        <div v-if="editable.split_method === 'single_line'" class="field" style="margin-top: 12px;">
          <label class="field-label">Charge the full shared cost to</label>
          <select v-model="editable.primary_property_id" class="setting-input">
            <option value="" disabled>Choose a property…</option>
            <option v-for="m in group.memberships" :key="m.id" :value="m.property_id">
              {{ m.property_name }}
            </option>
          </select>
        </div>
      </div>

      <!-- Owner details (read-only) -->
      <div class="detail-section">
        <div class="detail-section-title">Owner details</div>
        <div class="owner-box">
          <div>
            <div class="owner-label">Owner name</div>
            <div class="owner-value">{{ group.owner?.full_legal_name || '—' }}</div>
          </div>
          <div>
            <div class="owner-label">Email</div>
            <div class="owner-value">{{ group.owner?.primary_email || '—' }}</div>
          </div>
        </div>
        <div class="field-hint" style="margin-top: 6px;">
          Owner details are pulled from the property settings. To update, edit the individual property.
        </div>
      </div>

      <!-- Report format -->
      <div class="detail-section">
        <div class="detail-section-title">Report format</div>
        <div class="radio-group">
          <div
            class="radio-opt"
            :class="{ active: editable.report_format === 'per_property' }"
            @click="editable.report_format = 'per_property'"
          >
            <div class="radio-circle">
              <div v-if="editable.report_format === 'per_property'" class="radio-dot"></div>
            </div>
            <div>
              <div class="radio-title">Show each property separately</div>
              <div class="radio-desc">
                Each property has its own bookings section. Shared costs shown at the bottom.
              </div>
            </div>
          </div>
          <div
            class="radio-opt"
            :class="{ active: editable.report_format === 'combined_only' }"
            @click="editable.report_format = 'combined_only'"
          >
            <div class="radio-circle">
              <div v-if="editable.report_format === 'combined_only'" class="radio-dot"></div>
            </div>
            <div>
              <div class="radio-title">Combined totals only</div>
              <div class="radio-desc">
                Single set of figures for all properties combined. Simpler report, less detail.
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer actions -->
      <div class="btn-row-actions">
        <button class="btn btn-outline" @click="cancelEdit">Cancel</button>
        <button class="btn btn-primary" :disabled="!hasChanges || saving" @click="onSave">
          {{ saving ? 'Saving…' : 'Save group' }}
        </button>
      </div>

      <div v-if="errorMessage" class="error-banner">{{ errorMessage }}</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GroupCard',
  props: {
    group: { type: Object, required: true },
    availableProperties: { type: Array, default: () => [] },
  },
  emits: ['update', 'delete', 'add-member', 'remove-member'],
  data() {
    return {
      expanded: false,
      memberPick: '',
      saving: false,
      addingMember: false,
      addMemberError: '',
      errorMessage: '',
      editable: {
        split_method: this.group.split_method,
        report_format: this.group.report_format,
        primary_property_id: this.group.primary_property_id || '',
        shared_cleaning_fee: Number(this.group.shared_cleaning_fee || 0),
        shared_laundry_fee: Number(this.group.shared_laundry_fee || 0),
      },
    }
  },
  computed: {
    ownerLine() {
      const o = this.group.owner
      if (!o) return ''
      const parts = [o.full_legal_name, o.primary_email].filter(Boolean)
      return parts.join(' · ')
    },
    availableForAdd() {
      const memberIds = new Set(this.group.memberships.map((m) => m.property_id))
      return this.availableProperties.filter((p) => !memberIds.has(p.id))
    },
    hasChanges() {
      return (
        this.editable.split_method !== this.group.split_method ||
        this.editable.report_format !== this.group.report_format ||
        this.editable.primary_property_id !== (this.group.primary_property_id || '') ||
        Number(this.editable.shared_cleaning_fee || 0) !== Number(this.group.shared_cleaning_fee || 0) ||
        Number(this.editable.shared_laundry_fee || 0) !== Number(this.group.shared_laundry_fee || 0)
      )
    },
  },
  watch: {
    group: {
      handler(newGroup) {
        this.editable = {
          split_method: newGroup.split_method,
          report_format: newGroup.report_format,
          primary_property_id: newGroup.primary_property_id || '',
          shared_cleaning_fee: Number(newGroup.shared_cleaning_fee || 0),
          shared_laundry_fee: Number(newGroup.shared_laundry_fee || 0),
        }
      },
      deep: true,
    },
  },
  methods: {
    toggleExpand() {
      this.expanded = !this.expanded
      this.errorMessage = ''
      this.addMemberError = ''
    },
    cancelEdit() {
      this.editable = {
        split_method: this.group.split_method,
        report_format: this.group.report_format,
        primary_property_id: this.group.primary_property_id || '',
        shared_cleaning_fee: Number(this.group.shared_cleaning_fee || 0),
        shared_laundry_fee: Number(this.group.shared_laundry_fee || 0),
      }
      this.expanded = false
      this.errorMessage = ''
      this.addMemberError = ''
    },
    async onSave() {
      this.saving = true
      this.errorMessage = ''
      try {
        await new Promise((resolve, reject) => {
          this.$emit('update', this.group.id, { ...this.editable }, { resolve, reject })
        })
      } catch (e) {
        this.errorMessage = this.formatError(e)
      } finally {
        this.saving = false
      }
    },
    onDelete() {
      if (!confirm(`Remove the group "${this.group.name}"? This will not delete the underlying properties.`)) return
      this.$emit('delete', this.group.id)
    },
    onRemoveMember(propertyId) {
      this.$emit('remove-member', this.group.id, propertyId)
    },
    async onAddMember() {
      if (!this.memberPick || this.addingMember) return
      const pid = this.memberPick
      this.addingMember = true
      this.addMemberError = ''
      try {
        await new Promise((resolve, reject) => {
          this.$emit('add-member', this.group.id, pid, { resolve, reject })
        })
        // Only clear the picker on success — keep the selection on error so the
        // user can see what they tried to add.
        this.memberPick = ''
      } catch (e) {
        this.addMemberError = this.formatError(e)
      } finally {
        this.addingMember = false
      }
    },
    formatError(err) {
      if (err?.response?.data?.detail) return err.response.data.detail
      if (err?.message) return err.message
      return 'Something went wrong.'
    },
  },
}
</script>

<style scoped>
.group-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  margin-bottom: 12px;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}
.group-card:hover {
  border-color: #c7d2fe;
}
.group-card.expanded {
  border-color: #667eea;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.08);
}

.group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  cursor: pointer;
}
.group-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.group-icon {
  width: 34px;
  height: 34px;
  background: #eef2ff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #667eea;
  flex-shrink: 0;
}
.group-name {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
}
.group-subtitle {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}
.group-header-actions {
  display: flex;
  gap: 8px;
}

.group-detail {
  padding: 6px 18px 18px 18px;
  border-top: 1px solid #e5e7eb;
}

.detail-section {
  margin-top: 18px;
}
.detail-section-title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #6b7280;
  margin-bottom: 10px;
}

.group-props {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.group-prop-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 12px;
}
.prop-row-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.prop-badge {
  width: 26px;
  height: 26px;
  background: #eef2ff;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: #667eea;
}
.prop-name {
  font-size: 13px;
  color: #1f2937;
}
.empty-line {
  font-size: 12px;
  color: #9ca3af;
  font-style: italic;
  padding: 8px 0;
}
.add-member-row {
  display: flex;
  gap: 8px;
  margin-top: 8px;
  align-items: center;
}
.add-member-row select {
  flex: 1;
  margin: 0;
}

.field {
  display: flex;
  flex-direction: column;
}
.field-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}
.field-hint {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
  line-height: 1.4;
}

.shared-costs-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 14px;
}
@media (max-width: 720px) {
  .shared-costs-grid {
    grid-template-columns: 1fr;
  }
}
.input-prefix {
  display: flex;
  align-items: stretch;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  overflow: hidden;
  background: white;
}
.input-prefix > span {
  display: flex;
  align-items: center;
  padding: 0 10px;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 13px;
  font-weight: 600;
  border-right: 1px solid #d1d5db;
}
.input-prefix > input {
  flex: 1;
  border: none;
  outline: none;
  padding: 8px 10px;
  font-size: 13px;
  background: transparent;
}
.input-prefix:focus-within {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.12);
}

.owner-box {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.owner-label {
  font-size: 11px;
  color: #6b7280;
  margin-bottom: 3px;
}
.owner-value {
  font-size: 13px;
  font-weight: 500;
  color: #1f2937;
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.radio-opt {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1.5px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.2s ease;
}
.radio-opt:hover {
  border-color: #c7d2fe;
}
.radio-opt.active {
  border-color: #667eea;
  background: #eef2ff;
}
.radio-circle {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
}
.radio-opt.active .radio-circle {
  border-color: #667eea;
  background: #667eea;
}
.radio-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: white;
}
.radio-title {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
}
.radio-desc {
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
  line-height: 1.4;
}

.btn-row-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 18px;
}

.error-banner {
  margin-top: 10px;
  padding: 10px 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  border-radius: 8px;
  font-size: 13px;
}
.error-banner.inline-error {
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.45;
}

/* Borrow button styles from SettingsView's palette */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.2s ease;
}
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}
.btn-primary {
  background: #667eea;
  color: white;
  border-color: #667eea;
}
.btn-primary:hover:not(:disabled) {
  background: #5a67d8;
}
.btn-outline {
  background: transparent;
  color: #667eea;
  border-color: #667eea;
}
.btn-outline:hover:not(:disabled) {
  background: #667eea;
  color: white;
}
.btn-danger {
  background: transparent;
  color: #dc2626;
  border-color: #fecaca;
}
.btn-danger:hover:not(:disabled) {
  background: #fef2f2;
  border-color: #f87171;
}

.setting-input {
  padding: 10px 14px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  color: #1f2937;
  background: white;
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;
}
.setting-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}
</style>
